import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Plus, Trash2, X } from "lucide-react";
import { api } from "@/api/client";
import type { PromptMode, PromptType } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { useTopicsStore } from "@/stores/topics";
import { useBrandsStore } from "@/stores/brands";
import { useWorkspacesStore } from "@/stores/workspaces";
import { ModelSelector } from "@/components/compare/ModelSelector";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { formatTimesPerDay } from "@/lib/format";
import { cn } from "@/lib/utils";

const STEPS = ["Name it", "Prompts", "Competitors & tracking"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SENTIMENT_DIMENSIONS = ["recommendation", "sentiment", "visibility"];

type Cadence = "weekly" | "daily" | "monthly" | "times_per_day";
const CADENCE_OPTIONS: { value: Cadence; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "times_per_day", label: "Several times a day" },
];

interface DraftPrompt {
  key: number;
  name: string;
  template: string;
  type: PromptType;
  mode: PromptMode;
  dimensions: string;
  subtopic: string;
  suggested?: boolean;
}

const emptyPrompt = (key: number): DraftPrompt => ({
  key,
  name: "",
  template: "",
  type: "sentiment",
  mode: "blind",
  dimensions: "",
  subtopic: "",
});

const normalize = (s: string) => s.trim().toLowerCase() || "general";

/**
 * Template-based prompt suggestions from the topic name — pure, no LLM (same
 * approach as the pulse starter prompts). Two blind consumer-voice questions, a
 * field comparison, and a both-mode question, with the topic lowercased inline.
 */
function suggestedPrompts(topicName: string, startKey: number): DraftPrompt[] {
  const t = topicName.trim().toLowerCase();
  const base = (key: number, name: string, template: string, mode: PromptMode): DraftPrompt => ({
    key, name, template, type: "sentiment", mode, dimensions: "", subtopic: "", suggested: true,
  });
  return [
    base(startKey, "Best options", `What are the best ${t} options?`, "blind"),
    base(startKey + 1, "Who to go to", `Who should I go to for ${t}?`, "blind"),
    base(startKey + 2, "Strongest brand", `Which of these brands is strongest for ${t}?`, "field"),
    base(startKey + 3, "Top providers compared", `How do the leading ${t} providers compare?`, "both"),
  ];
}

/** Rough monthly run count per cadence — used only for the client-side estimate. */
function runsPerMonth(cadence: Cadence, timesPerDay: number): number {
  if (cadence === "daily") return 30;
  if (cadence === "monthly") return 1;
  if (cadence === "times_per_day") return timesPerDay * 30;
  return 52 / 12; // weekly
}

export function NewTopicWizard() {
  const navigate = useNavigate();
  const allTopics = useTopicsStore((s) => s.topics);
  const fetchTopics = useTopicsStore((s) => s.fetch);
  const refreshTopics = useTopicsStore((s) => s.refresh);
  const brands = useBrandsStore((s) => s.brands);
  const fetchBrands = useBrandsStore((s) => s.fetch);
  const workspace = useWorkspacesStore((s) => s.current);
  const fetchModels = useModelsStore((s) => s.fetch);

  useEffect(() => { fetchTopics(); fetchBrands(); fetchModels(); }, []);

  const [step, setStep] = useState(0);

  // Step 1
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Step 2
  const [prompts, setPrompts] = useState<DraftPrompt[]>([emptyPrompt(0)]);
  const nextKey = () => Math.max(0, ...prompts.map((p) => p.key)) + 1;
  // Prefill template suggestions once, the first time step 2 opens on an empty
  // prompt list — never again (even if the user deletes them all).
  const seededRef = useRef(false);
  useEffect(() => {
    if (step !== 1 || seededRef.current || !name.trim()) return;
    const empty = prompts.every((p) => !p.name.trim() && !p.template.trim());
    if (!empty) return;
    seededRef.current = true;
    setPrompts(suggestedPrompts(name, nextKey()));
  }, [step]);

  // Step 3
  const [selectedBrands, setSelectedBrands] = useState<Set<number>>(new Set());
  const [newBrandName, setNewBrandName] = useState("");
  const [addingBrand, setAddingBrand] = useState(false);
  const [fieldEnabled, setFieldEnabled] = useState(true);
  const [blindEnabled, setBlindEnabled] = useState(false);
  const [cadence, setCadence] = useState<Cadence>("weekly");
  const [hourUtc, setHourUtc] = useState(9);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timesPerDay, setTimesPerDay] = useState(4);
  const [models, setModels] = useState<Set<string>>(new Set());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useDefaultModelSeed(models.size === 0, (ids) => setModels(new Set(ids)));

  const primaryId = workspace?.primary_brand_id ?? null;
  const duplicate = name.trim() ? allTopics.find((t) => normalize(t.name) === normalize(name)) ?? null : null;

  const validPrompts = prompts.filter((p) => p.name.trim() && p.template.trim());
  const distinctSubtopics = useMemo(
    () => [...new Set(validPrompts.map((p) => p.subtopic.trim()).filter(Boolean))],
    [validPrompts],
  );

  // Client-side estimate — the topic doesn't exist yet, so we approximate the
  // server math: field runs cost one call per brand in the field (primary +
  // selected competitors), blind runs one call each. Labeled "~".
  const fieldSize = selectedBrands.size + 1;
  const fieldPromptCount = fieldEnabled ? validPrompts.filter((p) => p.mode === "field" || p.mode === "both").length : 0;
  const blindPromptCount = blindEnabled ? validPrompts.filter((p) => p.mode === "blind" || p.mode === "both").length : 0;
  const estimate = Math.round(
    (fieldPromptCount * fieldSize + blindPromptCount) * models.size * runsPerMonth(cadence, timesPerDay),
  );

  const canNext = step === 0 ? name.trim().length > 0 : step === 1 ? validPrompts.length > 0 : true;

  const updatePrompt = (key: number, patch: Partial<DraftPrompt>) =>
    setPrompts((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const toggleBrand = (id: number) =>
    setSelectedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });


  const addBrand = async () => {
    const value = newBrandName.trim();
    if (!value) return;
    setAddingBrand(true);
    setError("");
    try {
      const brand = await api.brands.create(value);
      await fetchBrands();
      setSelectedBrands((prev) => new Set(prev).add(brand.id));
      setNewBrandName("");
    } catch (e: any) {
      setError(e?.message || "Could not add the brand.");
    } finally {
      setAddingBrand(false);
    }
  };

  const submit = async (withTracking: boolean) => {
    setSubmitting(true);
    setError("");
    try {
      const topic = await api.topics.create({ name: name.trim(), description: description.trim() || undefined });
      const topicName = topic.name;

      // Distinct subtopic labels become subtopics; map label → id for prompt links.
      const subtopicByLabel = new Map<string, number>();
      for (const label of distinctSubtopics) {
        const st = await api.topics.addSubtopic(topic.id, label);
        subtopicByLabel.set(label, st.id);
      }

      for (const p of validPrompts) {
        const dims = p.type === "sentiment"
          ? SENTIMENT_DIMENSIONS
          : p.dimensions.split(",").map((d) => d.trim()).filter(Boolean);
        const created = await api.prompts.create(p.name.trim(), p.template.trim(), dims.length ? dims : SENTIMENT_DIMENSIONS, p.type, topicName);
        const subtopicId = p.subtopic.trim() ? subtopicByLabel.get(p.subtopic.trim()) ?? null : null;
        // Prompts default to field mode with no subtopic — only update when it differs.
        if (p.mode !== "field" || subtopicId !== null) {
          await api.prompts.update(created.id, { mode: p.mode, subtopic_id: subtopicId });
        }
      }

      await api.topics.setCompetitors(topic.id, [...selectedBrands]);

      if (withTracking) {
        await api.topics.setTracking(topic.id, {
          enabled: true,
          field_enabled: fieldEnabled,
          blind_enabled: blindEnabled,
          cadence,
          hour_utc: hourUtc,
          day_of_week: dayOfWeek,
          day_of_month: dayOfMonth,
          times_per_day: cadence === "times_per_day" ? timesPerDay : null,
          models: [...models],
        });
      }

      await refreshTopics();
      navigate(`/app/topics/${topic.id}`);
    } catch (e: any) {
      // The topic may already exist — keep wizard state so the user can retry.
      setError(e?.message || "Could not create the topic. Some steps may have completed — check your topics.");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Step indicator */}
      <div className="flex items-center gap-2 flex-wrap">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            {i > 0 && <span className="w-6 border-t border-dashed border-slate-300" />}
            <button
              type="button"
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={cn(
                "flex items-center gap-2 text-[12px] font-semibold",
                i === step ? "text-brand-950" : i < step ? "text-slate-500" : "text-slate-400",
                i < step && "cursor-pointer",
              )}
            >
              <span className={cn(
                "w-[22px] h-[22px] rounded-full border-[1.5px] flex items-center justify-center text-[11px] font-bold",
                i < step ? "bg-green-600 border-green-600 text-white" : i === step ? "bg-brand-600 border-brand-600 text-white" : "border-slate-300",
              )}>
                {i < step ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              {label}
            </button>
          </div>
        ))}
        <Link to="/app/health" className="ml-auto">
          <Button variant="ghost" size="sm">Cancel</Button>
        </Link>
      </div>

      {/* Step 1 — Name it */}
      {step === 0 && (
        <Card>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="topic-name">Topic name</Label>
              <Input id="topic-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Personal injury, Mesothelioma" />
            </div>
            <div>
              <Label htmlFor="topic-desc">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea id="topic-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this topic space covers." />
            </div>
            {duplicate && (
              <p className="text-[12.5px] text-amber-600">
                This topic already exists —{" "}
                <Link to={`/app/topics/${duplicate.id}`} className="font-medium underline">open it instead</Link>.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Prompts */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-[12.5px] text-slate-500">
            Add the questions this topic tracks. Write them the way a real customer would ask — blind prompts name no brands.
          </p>
          {prompts.map((p) => (
            <Card key={p.key}>
              <CardContent className="space-y-3">
                {p.suggested && (
                  <Badge variant="category" className="w-fit">Suggested</Badge>
                )}
                <div className="flex items-start gap-3">
                  <div className="flex-1 grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`p-name-${p.key}`}>Name</Label>
                      <Input id={`p-name-${p.key}`} value={p.name} onChange={(e) => updatePrompt(p.key, { name: e.target.value })} placeholder="e.g. Best firms" />
                    </div>
                    <div>
                      <Label htmlFor={`p-type-${p.key}`}>Type</Label>
                      <NativeSelect id={`p-type-${p.key}`} value={p.type} onChange={(e) => updatePrompt(p.key, { type: e.target.value as PromptType })}>
                        <option value="sentiment">Sentiment</option>
                        <option value="scoring">Scoring</option>
                      </NativeSelect>
                    </div>
                  </div>
                  {prompts.length > 1 && (
                    <Button variant="ghost" size="icon" className="mt-6 text-slate-400 hover:text-red-500" aria-label="Remove prompt" onClick={() => setPrompts((prev) => prev.filter((x) => x.key !== p.key))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label htmlFor={`p-template-${p.key}`}>Template</Label>
                  <Textarea id={`p-template-${p.key}`} rows={2} value={p.template} onChange={(e) => updatePrompt(p.key, { template: e.target.value })} placeholder="Write it the way a real customer would ask." />
                </div>
                {p.type === "scoring" && (
                  <div>
                    <Label htmlFor={`p-dims-${p.key}`}>Dimensions</Label>
                    <Input id={`p-dims-${p.key}`} value={p.dimensions} onChange={(e) => updatePrompt(p.key, { dimensions: e.target.value })} placeholder="Comma-separated, e.g. reputation, expertise" />
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`p-mode-${p.key}`}>Mode</Label>
                    <NativeSelect id={`p-mode-${p.key}`} value={p.mode} onChange={(e) => updatePrompt(p.key, { mode: e.target.value as PromptMode })}>
                      <option value="blind">Blind</option>
                      <option value="field">Field</option>
                      <option value="both">Both</option>
                    </NativeSelect>
                  </div>
                  <div>
                    <Label htmlFor={`p-sub-${p.key}`}>Subtopic <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input id={`p-sub-${p.key}`} value={p.subtopic} onChange={(e) => updatePrompt(p.key, { subtopic: e.target.value })} placeholder="e.g. pricing" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          <Button variant="secondary" onClick={() => setPrompts((prev) => [...prev, emptyPrompt(nextKey())])}>
            <Plus className="w-4 h-4 mr-1.5" />Add another prompt
          </Button>
          {distinctSubtopics.length > 0 && (
            <p className="text-[11.5px] text-slate-400">Subtopics to create: {distinctSubtopics.join(", ")}</p>
          )}
        </div>
      )}

      {/* Step 3 — Competitors & tracking */}
      {step === 2 && (
        <div className="space-y-4">
          {primaryId === null && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-700">
              No primary brand set for this workspace — field sentiment tracking needs one.{" "}
              <Link to="/app/brands" className="font-medium underline">Set a primary brand</Link>.
            </div>
          )}

          <Card>
            <CardContent className="space-y-2.5">
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Competitors</span>
              <div className="flex flex-wrap gap-1.5">
                {brands.filter((b) => b.id !== primaryId).map((b) => {
                  const on = selectedBrands.has(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBrand(b.id)}
                      aria-pressed={on}
                      className={cn(
                        "px-2.5 py-1 rounded-md text-[12.5px] font-medium border transition-colors",
                        on ? "bg-brand-50 text-brand-600 border-brand-100" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
                      )}
                    >
                      {b.name}
                    </button>
                  );
                })}
                {brands.filter((b) => b.id !== primaryId).length === 0 && (
                  <span className="text-[12px] text-slate-400">No other brands yet — add one below.</span>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Input
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBrand(); } }}
                  placeholder="Add a new competitor brand"
                />
                <Button type="button" variant="secondary" onClick={addBrand} disabled={!newBrandName.trim() || addingBrand}>
                  <Plus className="w-4 h-4 mr-1" />Brand
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3">
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Tracking</span>
              <div className="grid sm:grid-cols-2 gap-3">
                <LensToggle
                  label="Field sentiment"
                  desc="Asks about your brand directly."
                  checked={fieldEnabled}
                  onChange={setFieldEnabled}
                />
                <LensToggle
                  label="Blind visibility"
                  desc="Neutral queries, no brands named."
                  checked={blindEnabled}
                  onChange={setBlindEnabled}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="wiz-cadence">Cadence</Label>
                  <NativeSelect id="wiz-cadence" value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)}>
                    {CADENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </NativeSelect>
                </div>
                {cadence === "weekly" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="wiz-day">Run day</Label>
                    <NativeSelect id="wiz-day" value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value))}>
                      {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </NativeSelect>
                  </div>
                )}
                {cadence === "monthly" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="wiz-dom">Day of month</Label>
                    <NativeSelect id="wiz-dom" value={dayOfMonth} onChange={(e) => setDayOfMonth(parseInt(e.target.value))}>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                    </NativeSelect>
                  </div>
                )}
                {cadence === "times_per_day" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="wiz-tpd">Runs per day</Label>
                    <NativeSelect id="wiz-tpd" value={timesPerDay} onChange={(e) => setTimesPerDay(parseInt(e.target.value))}>
                      {Array.from({ length: 23 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{formatTimesPerDay(n)}</option>)}
                    </NativeSelect>
                  </div>
                )}
                {cadence !== "times_per_day" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="wiz-hour">Run hour (UTC)</Label>
                    <NativeSelect id="wiz-hour" value={hourUtc} onChange={(e) => setHourUtc(parseInt(e.target.value))}>
                      {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                    </NativeSelect>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Models</Label>
                <ModelSelector selected={models} onChange={setModels} />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-lg bg-brand-50 px-4 py-3 text-[12.5px] text-slate-600">
            <span>Estimated cost:</span>
            <b className="text-[13px] font-bold text-brand-600 tabular-nums">~{estimate} AI calls / month</b>
            <span className="text-slate-400">{fieldPromptCount + blindPromptCount} prompt{fieldPromptCount + blindPromptCount === 1 ? "" : "s"} × {models.size} model{models.size === 1 ? "" : "s"} × a field of {fieldSize} — approximate, computed before the topic exists.</span>
          </div>
        </div>
      )}

      {error && <p className="text-[12.5px] text-red-600">{error}</p>}

      {/* Footer nav */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || submitting}>
          Back
        </Button>
        {step < 2 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext}>Next</Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => submit(false)} disabled={submitting}>
              Create without tracking
            </Button>
            <Button onClick={() => submit(true)} disabled={submitting || models.size === 0}>
              {submitting ? "Creating…" : "Start tracking"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function LensToggle({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className={cn(
        "flex items-start gap-3 rounded-xl border-[1.5px] bg-white px-4 py-3 text-left transition-colors",
        checked ? "border-brand-500 bg-brand-50/60" : "border-slate-200 hover:bg-slate-50",
      )}
    >
      <span className={cn(
        "mt-0.5 w-[17px] h-[17px] rounded-[5px] border-[1.5px] flex items-center justify-center shrink-0",
        checked ? "bg-brand-600 border-brand-600" : "border-slate-300 bg-white",
      )}>
        {checked ? <Check className="w-3 h-3 text-white" /> : <X className="w-2.5 h-2.5 text-transparent" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-bold text-brand-950">{label}</span>
        <span className="block text-[12px] text-slate-500">{desc}</span>
      </span>
    </button>
  );
}
